import { useMemo } from 'react';

import type { User } from '../../admin-projects/types/response';
import {
  useGetAnnotatorsQuery,
  useGetSupervisorsQuery,
} from '../../create-project-wizard/api/createProjectApiSlice';

interface UseEligiblePeopleArgs {
  /** Ids already shown (persisted + added) and therefore not offered again. */
  excludedAnnotatorIds: Set<string>;
  excludedSupervisorIds: Set<string>;
  /** Skip fetching entirely (e.g. when not in edit mode). */
  skip?: boolean;
}

interface UseEligiblePeopleResult {
  eligibleAnnotators: User[];
  eligibleSupervisors: User[];
  isAnnotatorsLoading: boolean;
  isSupervisorsLoading: boolean;
}

/**
 * Returns the annotators/supervisors that can still be added — the full system
 * lists (from the create-project endpoints) minus the ids already in use — along
 * with each list's loading state for skeleton rendering.
 */
export const useEligiblePeople = ({
  excludedAnnotatorIds,
  excludedSupervisorIds,
  skip = false,
}: UseEligiblePeopleArgs): UseEligiblePeopleResult => {
  const {
    data: annotatorsData,
    isLoading: isAnnotatorsLoadingRaw,
    isFetching: isAnnotatorsFetching,
  } = useGetAnnotatorsQuery(undefined, { skip });
  const {
    data: supervisorsData,
    isLoading: isSupervisorsLoadingRaw,
    isFetching: isSupervisorsFetching,
  } = useGetSupervisorsQuery(undefined, { skip });

  return useMemo(
    () => ({
      eligibleAnnotators: (annotatorsData?.data ?? []).filter(
        (a) => !excludedAnnotatorIds.has(a.id)
      ),
      eligibleSupervisors: (supervisorsData?.data ?? []).filter(
        (s) => !excludedSupervisorIds.has(s.id)
      ),
      isAnnotatorsLoading: isAnnotatorsLoadingRaw || isAnnotatorsFetching,
      isSupervisorsLoading: isSupervisorsLoadingRaw || isSupervisorsFetching,
    }),
    [
      annotatorsData,
      supervisorsData,
      excludedAnnotatorIds,
      excludedSupervisorIds,
      isAnnotatorsLoadingRaw,
      isAnnotatorsFetching,
      isSupervisorsLoadingRaw,
      isSupervisorsFetching,
    ]
  );
};
